import React, {useEffect, useState} from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Grid,
    Typography,
    Box,
    Autocomplete,
    Chip,
    Alert,
    Switch,
    FormControlLabel,
    Stack
} from '@mui/material';
import {
    Add as AddIcon,
    Schedule as ScheduleIcon,
    Label as LabelIcon
} from '@mui/icons-material';
import {useAuth} from "../../../hooks/useAuth";

const CampaignFormDialog = ({
                                open,
                                onClose,
                                isEditMode,
                                isViewOnlyMode = false,
                                formData,
                                handleInputChange,
                                handleSave,
                                availableInstances,
                                campaignContacts,
                                onOpenContactSelection,
                                loading,
                                availableContactTags = [] // Добавляем доступные теги
                            }) => {
    const { user } = useAuth();
    const userTimezone = user?.timezone || 'UTC+3';

    console.log('CampaignFormDialog received formData:', formData);
    console.log('target_tags type:', typeof formData.target_tags);
    console.log('target_tags value:', formData.target_tags);
    console.log('stop_tags type:', typeof formData.stop_tags);
    console.log('stop_tags value:', formData.stop_tags);

    // Helper function to determine if a field should be disabled
    const isFieldDisabled = loading || isViewOnlyMode;

    // Local toggle state to control End Date enable/disable independently of value presence
    const [endDateEnabled, setEndDateEnabled] = useState(!!formData.end_date);

    // Keep local toggle in sync if formData.end_date changes from outside (e.g., when opening dialog)
    useEffect(() => {
        setEndDateEnabled(!!formData.end_date);
    }, [formData.end_date]);

    // Format working hours for display
    const formatWorkingHours = (start, end) => {
        if (start === undefined || end === undefined) return '';
        return `${String(start).padStart(2, '0')}:00 - ${String(end).padStart(2, '0')}:00 (${userTimezone})`;
    };

    // Обработчики для тегов
    const handleTargetTagsChange = (event, newValue) => {
        handleInputChange({
            target: {
                name: 'target_tags',
                value: newValue
            }
        });
    };

    const handleStopTagsChange = (event, newValue) => {
        handleInputChange({
            target: {
                name: 'stop_tags',
                value: newValue
            }
        });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            disablePortal={false}
            keepMounted={false}
            disableEnforceFocus={false}
            disableAutoFocus={false}
            disableRestoreFocus={false}
        >
            <DialogTitle>
                {isViewOnlyMode ? 'View Campaign' : isEditMode ? 'Edit Campaign' : 'Create New Campaign'}
            </DialogTitle>
            {console.log(formData)}
            <DialogContent>
                <Grid container spacing={2}>
                    {/* Campaign Details Section */}
                    <Grid item xs={12}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Campaign Name"
                                    name="name"
                                    value={formData.name || ''}
                                    onChange={handleInputChange}
                                    required
                                    disabled={isFieldDisabled}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    name="description"
                                    value={formData.description || ''}
                                    onChange={handleInputChange}
                                    multiline
                                    rows={2}
                                    disabled={isFieldDisabled}
                                />
                            </Grid>
                            {/* Two-column layout for scheduling fields */}
                            <Grid item xs={12}>
                                <Grid container spacing={2}>
                                    {/* Left column: Start and End date */}
                                    <Grid item xs={12} sm={6}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Start Date"
                                                    name="start_date"
                                                    type="datetime-local"
                                                    value={formData.start_date || ''}
                                                    onChange={handleInputChange}
                                                    InputLabelProps={{ shrink: true }}
                                                    disabled={isFieldDisabled}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="End Date"
                                                    name="end_date"
                                                    type="datetime-local"
                                                    value={formData.end_date || ''}
                                                    onChange={handleInputChange}
                                                    InputLabelProps={{ shrink: true }}
                                                    disabled={isFieldDisabled || !endDateEnabled}
                                                    helperText={endDateEnabled ? 'Campaign will stop at this date/time' : 'Off: campaign finishes after all messages are sent'}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    {/* Right column: Delay and End-date toggle */}
                                    <Grid item xs={12} sm={6}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Sending delay (seconds)"
                                                    name="sending_delay"
                                                    type="number"
                                                    value={formData.sending_delay || 0}
                                                    onChange={handleInputChange}
                                                    inputProps={{ min: 0, max: 86400, step: 1 }}
                                                    helperText="Delay between sending messages in seconds"
                                                    disabled={isFieldDisabled}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={endDateEnabled}
                                                            onChange={(e) => {
                                                                const checked = e.target.checked;
                                                                setEndDateEnabled(checked);
                                                                if (!checked) {
                                                                    handleInputChange({ target: { name: 'end_date', value: '' } });
                                                                } else if (!formData.end_date) {
                                                                    handleInputChange({ target: { name: 'end_date', value: '' } });
                                                                }
                                                            }}
                                                            disabled={loading || isViewOnlyMode}
                                                        />
                                                    }
                                                    label="Use end date"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Working Hours Section with Timezone Info */}
                            <Grid item xs={12}>
                                <Alert
                                    severity="info"
                                    icon={<ScheduleIcon />}
                                    sx={{ mb: 2 }}
                                >
                                    <Typography variant="body2">
                                        <strong>Working Hours Timezone:</strong> {userTimezone}
                                        {formData.working_hours_start !== undefined && formData.working_hours_end !== undefined && (
                                            <>
                                                <br />
                                                <strong>Current setting:</strong> {formatWorkingHours(formData.working_hours_start, formData.working_hours_end)}
                                            </>
                                        )}
                                    </Typography>
                                </Alert>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={`Working Hours Start (${userTimezone})`}
                                    name="working_hours_start"
                                    type="number"
                                    value={formData.working_hours_start ?? 9}
                                    onChange={handleInputChange}
                                    inputProps={{ min: 0, max: 23, step: 1 }}
                                    helperText={`Hour of day (0-23) in ${userTimezone} when messages can start being sent`}
                                    disabled={isFieldDisabled}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={`Working Hours End (${userTimezone})`}
                                    name="working_hours_end"
                                    type="number"
                                    value={formData.working_hours_end ?? 18}
                                    onChange={handleInputChange}
                                    inputProps={{ min: 0, max: 23, step: 1 }}
                                    helperText={`Hour of day (0-23) in ${userTimezone} when to stop sending messages`}
                                    disabled={isFieldDisabled}
                                />
                            </Grid>

                            {/* НОВАЯ СЕКЦИЯ: Теги кампании */}
                            <Grid item xs={12}>
                                <Alert
                                    severity="info"
                                    icon={<LabelIcon />}
                                    sx={{ mb: 2 }}
                                >
                                    <Typography variant="body2">
                                        <strong>Tags Configuration:</strong> Configure tags for contact management
                                    </Typography>
                                </Alert>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    multiple
                                    freeSolo
                                    options={availableContactTags}
                                    getOptionLabel={(option) => typeof option === 'string' ? option : option.name || option.label || String(option)}
                                    value={formData.target_tags || []}
                                    onChange={handleTargetTagsChange}
                                    disabled={isFieldDisabled}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                variant="outlined"
                                                color="primary"
                                                label={typeof option === 'string' ? option : option.name || option.label || String(option)}
                                                {...getTagProps({ index })}
                                                key={index}
                                                disabled={isFieldDisabled}
                                            />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Target Tags"
                                            placeholder="Tags to set for contacted numbers"
                                            helperText="Tags that will be assigned to contacts when message is sent"
                                            disabled={isFieldDisabled}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    multiple
                                    freeSolo
                                    options={availableContactTags}
                                    getOptionLabel={(option) => typeof option === 'string' ? option : option.name || option.label || String(option)}
                                    value={formData.stop_tags || ['STOP']}
                                    onChange={handleStopTagsChange}
                                    disabled={isFieldDisabled}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => {
                                            const tagValue = typeof option === 'string' ? option : option.name || option.label || String(option);
                                            return (
                                                <Chip
                                                    variant="outlined"
                                                    color={"warning"}
                                                    label={tagValue}
                                                    {...getTagProps({ index })}
                                                    key={index}
                                                    disabled={isFieldDisabled}
                                                    deleteIcon={undefined}
                                                />
                                            );
                                        })
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Stop Tags"
                                            placeholder="Tags that prevent message sending"
                                            helperText="Contacts with these tags will be skipped (STOP is always included)"
                                            disabled={isFieldDisabled}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Autocomplete
                                    multiple
                                    options={availableInstances || []}
                                    getOptionLabel={(option) => option.alias || `Instance ${option.id}`}
                                    value={availableInstances.filter(instance =>
                                        (formData.instances || []).includes(instance.id)
                                    )}
                                    onChange={(event, newValue) => {
                                        handleInputChange({
                                            target: {
                                                name: 'instances',
                                                value: newValue.map(instance => instance.id)
                                            }
                                        });
                                    }}
                                    disabled={isFieldDisabled}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                variant="outlined"
                                                label={option.alias || `Instance ${option.id}`}
                                                {...getTagProps({ index })}
                                                key={option.id}
                                                disabled={isFieldDisabled}
                                            />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Instances"
                                            placeholder="Choose instances for this campaign"
                                            disabled={isFieldDisabled}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Message Content"
                                    name="message_content"
                                    value={formData.message_content || ''}
                                    onChange={handleInputChange}
                                    multiline
                                    rows={4}
                                    placeholder="Enter your message template here..."
                                    disabled={isFieldDisabled}
                                />
                            </Grid>

                            {/* New Contact Selection Button */}
                            <Grid item xs={12}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    size="large"
                                    onClick={onOpenContactSelection}
                                    sx={{ py: 2, fontSize: '1rem', fontWeight: 600 }}
                                    startIcon={<AddIcon />}
                                    disabled={isFieldDisabled}
                                >
                                    {campaignContacts && campaignContacts.length > 0
                                        ? `Selected Contacts: ${campaignContacts.length}`
                                        : 'Select Contacts'}
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    {isViewOnlyMode ? 'Close' : 'Cancel'}
                </Button>
                {!isViewOnlyMode && (
                    <Button onClick={handleSave} variant="contained" disabled={loading}>
                        {loading ? 'Saving...' : isEditMode ? 'Update Campaign' : 'Create Campaign'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CampaignFormDialog;